import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useDictionary } from '../../../../../utilities/hooks/UseDictionary';
import { changeProviderStatus } from './networking';
import ProfileImage from '../../../common/ProfileImage';
import dots from '../../../../../assets/icons/ic_three_dots.svg';
import { ReactComponent as EyeIcon } from '../../../../../assets/icons/ic_eye.svg';
import { ReactComponent as CloseIcon } from '../../../../../assets/icons/ic_close.svg';
import { ReactComponent as StarIcon } from '../../../../../assets/icons/ic_star.svg';
import useComponentVisible from '../../../../../utilities/hooks/useComponentVisible';
import IconButton from '../../atom/IconButton.js';

const ProviderApplicationItem = ({
	item,
	rowNumber,
	onProviderClicked,
	onUpdated,
	setLoaderVisibility
}) => {
	const dictionary = useDictionary();

	const { ref, isComponentVisible, setIsComponentVisible } =
		useComponentVisible(false);

	const statuses = {
		1: { label: dictionary.unverified, class: 'experience-item-label-error' },
		2: { label: dictionary.pending, class: 'experience-item-label-unavailable' },
		3: { label: dictionary.verified, class: 'experience-item-label-active' },
		4: { label: dictionary.rejected, class: 'experience-item-label-error' }
	};

	const status = statuses[item.verification_status.value];
	const canChangeStatus = item.verification_status.value !== '1';
	const isVerified = item.verification_status.value === '3';

	const isSubscription =
		item?.is_subscription === true ||
		item?.is_subscription === 1 ||
		item?.is_subscription === '1';
	const isEnterprise = item?.plan === 'enterprise';

	const isPlaceMatched =
		item?.is_matched === true ||
		item?.is_matched === 1 ||
		item?.is_matched === '1';

	const totalReviews = Number(item?.total_reviews ?? 0);
	const avgReviews = Number(item?.avg_reviews ?? 0);

	const onContextClicked = (event) => {
		event.stopPropagation();
		setIsComponentVisible(!isComponentVisible);
	};

	const onViewClicked = (event) => {
		event.stopPropagation();
		setIsComponentVisible(false);
		onProviderClicked(item);
	};

	const changeProviderStatusAsync = async (event) => {
		event.stopPropagation();
		setIsComponentVisible(false);
		setLoaderVisibility(true);
		try {
			await changeProviderStatus(item.id, !isVerified);
			onUpdated();
		} catch (error) {
			setLoaderVisibility(false);
		}
	};

	return (
		<tr
			style={{ cursor: 'pointer' }}
			onClick={() => onProviderClicked(item)}
			data-testid={'provider-application-row-' + item.id}
		>
			<td>
				<div className="font-family-roboto">{rowNumber}</div>
			</td>
			<td>
				<div
					className="booking-customer-container m-zero font-family-roboto"
					style={{ gap: '8px' }}
				>
					<ProfileImage
						name={`${item?.first_name} ${item?.last_name}`}
						photo={item?.profile_photo?.path ? item.profile_photo.path : null}
						size={{ width: 24, height: 24, fontSize: 11, lineHeight: 14 }}
					/>
					<div
						className="provider-booking-customer-name font-family-roboto truncate"
						title={`${item?.first_name} ${item?.last_name}`}
					>
						{`${item?.first_name} ${item?.last_name}`}
					</div>
				</div>
			</td>
			<td>
				<div className="font-family-roboto">{item.email ? item.email : '-'}</div>
			</td>
			<td>
				<div className="font-family-roboto">{item.phone ? item.phone : '-'}</div>
			</td>
			<td>
				<div className="font-family-roboto truncate" title={item.brand_name}>
					{item.brand_name ? item.brand_name : '-'}
				</div>
			</td>
			<td>
				<div
					className={'experience-item-label ' + status.class}
					style={{ width: '100px', textAlign: 'center' }}
					data-testid="status-label"
				>
					{status.label}
				</div>
			</td>
			<td>
				<div className="font-family-roboto" data-testid="provider-plan">
					{isEnterprise
						? dictionary.enterprise_plan
						: isSubscription
							? dictionary.business_plan
							: dictionary.basic}
				</div>
			</td>
			<td>
				<div className="font-family-roboto" data-testid="provider-place-matched">
					{isPlaceMatched ? dictionary.yes : dictionary.no}
				</div>
			</td>
			<td>
				<div className="font-family-roboto" data-testid="provider-experiences">
					{item.experience_created ?? 0}
				</div>
			</td>
			<td>
				<div className="font-family-roboto" data-testid="provider-bookings">
					{item.total_bookings ?? 0}
				</div>
			</td>
			<td>
				<div
					className="font-family-roboto d-flex items-center"
					style={{ gap: '4px' }}
					data-testid="provider-rating"
				>
					{totalReviews > 0 ? (
						<>
							<StarIcon width={14} height={14} />
							<span>{avgReviews.toFixed(1)}</span>
							<span style={{ color: 'var(--placeholder-color)' }}>
								({totalReviews})
							</span>
						</>
					) : (
						'-'
					)}
				</div>
			</td>
			<td>
				<div className="font-family-roboto">
					{moment.unix(item.created_at).format('MMM DD, YYYY')}
				</div>
			</td>
			<td>
				<div className="bookings-actions-wrapper">
					<IconButton
						onClickIcon={onContextClicked}
						activeState={isComponentVisible}
						styleObject={{ width: '32px', height: '32px', borderRadius: '6px' }}
					>
						<img src={dots} alt="" />
					</IconButton>

					<div ref={ref} className="booking-action-menus-container">
						{isComponentVisible ? (
							<div className="booking-actions-container">
								<div
									className="booking-action-container"
									data-testid="provider-action-view"
									onClick={onViewClicked}
								>
									<EyeIcon />
									<div>{dictionary.view}</div>
								</div>
								{canChangeStatus ? (
									<div
										className="booking-action-container"
										data-testid="change-status-button"
										onClick={changeProviderStatusAsync}
									>
										<CloseIcon />
										<div>
											{isVerified
												? dictionary.unverify
												: dictionary.verify}
										</div>
									</div>
								) : null}
							</div>
						) : null}
					</div>
				</div>
			</td>
		</tr>
	);
};

ProviderApplicationItem.propTypes = {
	item: PropTypes.object.isRequired,
	rowNumber: PropTypes.number,
	onProviderClicked: PropTypes.func.isRequired,
	onUpdated: PropTypes.func.isRequired,
	setLoaderVisibility: PropTypes.func.isRequired
};

export default ProviderApplicationItem;
